import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription, finalize, interval } from 'rxjs';
import { HotspotSyncRun } from '../../core/models/hotspot-passenger.model';
import { HotspotPassengerService } from '../../core/services/hotspot-passenger.service';

type SyncResource = 'Passengers' | 'Sessions';

@Component({
  selector: 'app-hotspot-scheduled-sync',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hotspot-scheduled-sync.component.html',
  styleUrls: ['./hotspot-scheduled-sync.component.css']
})
export class HotspotScheduledSyncComponent implements OnInit, OnDestroy {
  private hotspotPassengerService = inject(HotspotPassengerService);
  private refreshSubscription?: Subscription;

  passengerRuns: HotspotSyncRun[] = [];
  sessionRuns: HotspotSyncRun[] = [];
  allRuns: HotspotSyncRun[] = [];
  allRunTotal = 0;
  passengerRunTotal = 0;
  sessionRunTotal = 0;
  errorMessage = '';
  allRunsError = '';
  passengerRunsError = '';
  sessionRunsError = '';
  isLoadingAllRuns = false;
  isLoadingPassengerRuns = false;
  isLoadingSessionRuns = false;
  lastLoadedAt?: Date;

  ngOnInit(): void {
    this.loadHistory();
    this.refreshSubscription = interval(30000).subscribe(() => this.loadHistory(false));
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  loadHistory(showLoading = true): void {
    this.errorMessage = '';
    this.loadAllRuns(showLoading);
    this.loadPassengerRuns(showLoading);
    this.loadSessionRuns(showLoading);
  }

  get isLoading(): boolean {
    return this.isLoadingAllRuns || this.isLoadingPassengerRuns || this.isLoadingSessionRuns;
  }

  private loadAllRuns(showLoading: boolean): void {
    this.isLoadingAllRuns = showLoading;
    this.allRunsError = '';

    this.hotspotPassengerService.getSyncRuns(undefined, 1, 10).pipe(
      finalize(() => this.isLoadingAllRuns = false)
    ).subscribe({
      next: response => {
        this.allRuns = response.data || [];
        this.allRunTotal = response.meta?.total || 0;
        this.markRefreshed();
      },
      error: error => {
        console.error('Unable to load all hotspot sync history:', error);
        this.allRunsError = this.getLoadError(error, 'Unable to load all sync runs');
      }
    });
  }

  private loadPassengerRuns(showLoading: boolean): void {
    this.isLoadingPassengerRuns = showLoading;
    this.passengerRunsError = '';

    this.hotspotPassengerService.getSyncRuns('Passengers', 1, 10).pipe(
      finalize(() => this.isLoadingPassengerRuns = false)
    ).subscribe({
      next: response => {
        this.passengerRuns = response.data || [];
        this.passengerRunTotal = response.meta?.total || 0;
        this.markRefreshed();
      },
      error: error => {
        console.error('Unable to load passenger hotspot sync history:', error);
        this.passengerRunsError = this.getLoadError(error, 'Unable to load passenger sync runs');
      }
    });
  }

  private loadSessionRuns(showLoading: boolean): void {
    this.isLoadingSessionRuns = showLoading;
    this.sessionRunsError = '';

    this.hotspotPassengerService.getSyncRuns('Sessions', 1, 10).pipe(
      finalize(() => this.isLoadingSessionRuns = false)
    ).subscribe({
      next: response => {
        this.sessionRuns = response.data || [];
        this.sessionRunTotal = response.meta?.total || 0;
        this.markRefreshed();
      },
      error: error => {
        console.error('Unable to load session hotspot sync history:', error);
        this.sessionRunsError = this.getLoadError(error, 'Unable to load session sync runs');
      }
    });
  }

  getRunId(run: HotspotSyncRun): string {
    return String(run.hotspotSyncRunID || run.id || '-');
  }

  getProcessed(run: HotspotSyncRun): string | number {
    return run.processed ?? '-';
  }

  getRequested(run: HotspotSyncRun): string | number {
    return run.requested ?? run.requestedLimit ?? run.perPage ?? '-';
  }

  getSyncType(run: HotspotSyncRun): string {
    return run.syncType || '-';
  }

  getCreatedProfiles(run: HotspotSyncRun): string | number {
    return run.createdProfiles ?? run.createdPassengerProfiles ?? '-';
  }

  getCreatedMappings(run: HotspotSyncRun): string | number {
    return run.createdAssetMappings ?? '-';
  }

  getErrorCount(run: HotspotSyncRun): string | number {
    return run.errorCount ?? run.errors ?? '-';
  }

  getRunDetail(run: HotspotSyncRun): string {
    if (run.errorMessage) {
      return run.errorMessage;
    }

    const parts = [
      run.provider ? `Provider: ${run.provider}` : null,
      run.page !== undefined && run.page !== null ? `Offset/page: ${run.page}` : null,
      run.perPage !== undefined && run.perPage !== null ? `Batch size: ${run.perPage}` : null
    ].filter(Boolean);

    return parts.join(' | ') || '-';
  }

  getRunTime(run: HotspotSyncRun, key: 'started' | 'completed'): string {
    return this.formatDate(key === 'started'
      ? run.startedAt || run.createdAt
      : run.completedAt);
  }

  formatDate(value?: string): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  }

  trackRun(_index: number, run: HotspotSyncRun): string {
    return String(run.hotspotSyncRunID || run.id || _index);
  }

  getResourceLabel(resource: SyncResource): string {
    return resource === 'Passengers' ? 'Passenger sync runs' : 'Session sync runs';
  }

  getTotalLabel(total: number, shown: number): string {
    return `${shown} shown / ${total} total`;
  }

  private markRefreshed(): void {
    this.lastLoadedAt = new Date();
  }

  private getLoadError(error: any, fallback: string): string {
    const detail = error?.error?.message || error?.message;
    return detail ? `${fallback}: ${detail}` : fallback;
  }
}
